import {
  type ComponentType,
  type CSSProperties,
  type Ref,
  memo,
  Suspense,
  useState,
} from "react";
import CSSTransition, {
  type CSSTransitionProps,
} from "react-transition-group/CSSTransition";
import TransitionGroup from "react-transition-group/TransitionGroup";
import NodeDiv from "@/common/components/NodeDiv";
import { createNodeRefs } from "@/common/nodeRefs";
import { shopWindowTypes, valueNames } from "@/modules/Constants";
import { usePlayerPixieHost } from "@/modules/playerPixieHost";
import MerchantCtaActionSheet from "@/components/shared/MerchantCtaActionSheet";
import { PixieChunk } from "@/components/shopwindow/ask/pixieChunk";
import ListShopWindow from "@/components/shopwindow/ListShopWindow";
import MethodShopWindow from "@/components/shopwindow/MethodShopWindow";
import ProductShopWindow from "@/components/shopwindow/ProductShopWindow";
import TestShopWindow from "@/components/shopwindow/TestShopWindow";
import type ShopWindowConfig from "@/plugin/ShopWindowConfig";
import type { PixieHost, PixieLayout } from "@/types/pixieHost";
import type { ExperienceState } from "@/types/state";

type ShopWindowEventHandler = (event: Record<string, unknown>) => void;

type ShopWindowChildProps = {
  nodeRef: Ref<HTMLDivElement>;
  experienceState: ExperienceState;
  config: ShopWindowConfig;
  hasHeader?: boolean;
  hasExitButton?: boolean;
  onApplicationEvent: ShopWindowEventHandler;
};

type TransitionProps = Omit<CSSTransitionProps, "classNames" | "timeout">;

const FadeTransition = (props: TransitionProps) => (
  <CSSTransition {...props} classNames="shop-window" timeout={200} />
);

const ActionSheetTransition = (props: TransitionProps) => (
  <CSSTransition {...props} classNames="action-sheet" timeout={300} />
);

type ShopWindowProps = {
  experienceState: ExperienceState;
  hasHeader?: boolean;
  hasExitButton?: boolean;
  askInWorkspace?: boolean;
  askLayout?: PixieLayout;
  isOverlay?: boolean;
  freeze?: boolean;
  style?: CSSProperties;
  nodeRef?: Ref<HTMLDivElement>;
  onApplicationEvent: ShopWindowEventHandler;
};

function ShopWindowView({
  experienceState,
  hasHeader,
  hasExitButton,
  askInWorkspace,
  askLayout,
  isOverlay,
  style,
  nodeRef,
  onApplicationEvent,
}: ShopWindowProps) {
  // One stable nodeRef per tab and for the action sheet, so each keeps its own
  // while it exits.
  const [nodeRefFor] = useState(() => createNodeRefs<HTMLDivElement>());

  const getShopWindow = (
    Window: ComponentType<ShopWindowChildProps>,
    shopWindowConfig: ShopWindowConfig,
  ) => (
    <FadeTransition
      key={`${shopWindowConfig.name}-shop-window`}
      nodeRef={nodeRefFor(shopWindowConfig.name)}
    >
      <Window
        nodeRef={nodeRefFor(shopWindowConfig.name)}
        experienceState={experienceState}
        config={shopWindowConfig}
        hasHeader={hasHeader}
        hasExitButton={hasExitButton}
        onApplicationEvent={onApplicationEvent}
      />
    </FadeTransition>
  );

  const getAskShopWindow = (
    shopWindowConfig: ShopWindowConfig,
    host: PixieHost,
  ) => (
    <FadeTransition
      key={`${shopWindowConfig.name}-shop-window`}
      nodeRef={nodeRefFor(shopWindowConfig.name)}
    >
      <Suspense fallback={null}>
        <PixieChunk.Component
          nodeRef={nodeRefFor(shopWindowConfig.name)}
          host={host}
          hasHeader={hasHeader}
          hasExitButton={hasExitButton}
        />
      </Suspense>
    </FadeTransition>
  );

  const getActionSheet = () => (
    <ActionSheetTransition
      key="action-sheet"
      nodeRef={nodeRefFor("action-sheet")}
    >
      <MerchantCtaActionSheet
        nodeRef={nodeRefFor("action-sheet")}
        experienceState={experienceState}
        onApplicationEvent={onApplicationEvent}
      />
    </ActionSheetTransition>
  );

  const applicationData = experienceState.applicationData;
  let shopWindowConfig = applicationData.getValue(
    valueNames.SHOP_WINDOW_CONFIG,
  ) as ShopWindowConfig | null | undefined;
  if (
    askInWorkspace &&
    shopWindowConfig &&
    shopWindowConfig.type === shopWindowTypes.ASK &&
    applicationData.isLandscape()
  ) {
    shopWindowConfig = isOverlay
      ? null
      : (applicationData.getValue(valueNames.SHOP_WINDOW_DEFAULT_CONFIG) as
          | ShopWindowConfig
          | null
          | undefined);
  }
  const askHost = usePlayerPixieHost({
    experienceState,
    onApplicationEvent,
    config:
      shopWindowConfig && shopWindowConfig.type === shopWindowTypes.ASK
        ? shopWindowConfig
        : undefined,
    layout: askLayout,
  });
  let shopWindow;

  const hasShopWindow =
    shopWindowConfig &&
    (isOverlay === true
      ? (applicationData.isPortrait() && shopWindowConfig.isPortraitOverlay) ||
        applicationData.isLandscape()
      : applicationData.isLandscape() ||
        shopWindowConfig.isPortraitOverlay === false);

  if (shopWindowConfig && hasShopWindow) {
    const shopWindowType = shopWindowConfig.type;

    if (shopWindowType === shopWindowTypes.METHOD) {
      shopWindow = getShopWindow(MethodShopWindow, shopWindowConfig);
    } else if (shopWindowType === shopWindowTypes.PRODUCT) {
      shopWindow = getShopWindow(ProductShopWindow, shopWindowConfig);
    } else if (shopWindowType === shopWindowTypes.LIST) {
      shopWindow = getShopWindow(ListShopWindow, shopWindowConfig);
    } else if (shopWindowType === shopWindowTypes.TEST) {
      shopWindow = getShopWindow(TestShopWindow, shopWindowConfig);
    } else if (shopWindowType === shopWindowTypes.ASK) {
      shopWindow = getAskShopWindow(shopWindowConfig, askHost);
    } else {
      shopWindow = null;
    }
  }

  const hasActionSheet =
    applicationData.showArticleActionSheet() && applicationData.isLandscape();
  const actionSheet = hasActionSheet ? getActionSheet() : null;

  if (hasShopWindow || isOverlay !== true) {
    return (
      <TransitionGroup
        component={NodeDiv}
        nodeRef={nodeRef}
        className="shop-window tw:[.experience-container>&]:absolute tw:[.experience-container>&]:inset-0 tw:[.shop-window-panel>&]:relative tw:[.shop-window-panel>&]:grow"
        style={style}
      >
        {shopWindow}
        {actionSheet}
      </TransitionGroup>
    );
  }

  return null;
}

// While `freeze` is set the window keeps its last render, as the class's
// shouldComponentUpdate did; memo skips the render when this returns true.
const ShopWindow = memo(ShopWindowView, (_prev, next) => Boolean(next.freeze));

export default ShopWindow;
